import { clearAuthStorage, getSessionToken } from './authStorage'
import { signOutSession } from './authApi'
import { cancelScheduledRefresh } from './tokenRefresh'
import useLoginStore from '@/store/LoginStore'
import { logger } from '@/lib/monitoring/logger'
import { LogCategory } from '@/lib/monitoring/types'
import {
  LEGACY_PURGE_METADATA_KEY,
  PURGE_METADATA_KEY_PREFIX,
} from '@/lib/db/rxdb/cleanup/purgeService'

// Persisted Zustand stores that hold per-user state. Cleared on logout so a
// different account logging into the same browser doesn't inherit filters,
// view preferences, or language/theme from the previous user.
const PERSISTED_STORE_KEYS = [
  'moly',
  'moly-project-filters-v2',
  'moly-project-view-preferences',
  'moly-user-preferences',
  'moly-hide-completed',
  'moly-view-mode',
]

// Direct-write localStorage keys (not backed by a Zustand store).
// rxdb_purge_metadata is the legacy un-prefixed key; per-workspace keys
// (rxdb_purge_metadata_<workspaceId>) are swept separately below.
const DIRECT_STORAGE_KEYS = [LEGACY_PURGE_METADATA_KEY]

// Logging out must work offline, so the server-side revoke gets a short leash.
const SIGN_OUT_TIMEOUT_MS = 3000

/**
 * Revoke the session on the server (POST /auth/sign-out). Best effort: offline
 * or failing, the session just ages out server-side; the local copy is cleared
 * regardless.
 */
const revokeServerSession = async (sessionToken: string | null): Promise<void> => {
  if (!sessionToken) return
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), SIGN_OUT_TIMEOUT_MS)
  try {
    await signOutSession(sessionToken, controller.signal)
  } catch (error) {
    logger.logEvent(LogCategory.AUTH_SESSION, 'logout.server_sign_out_failed', {
      message: error instanceof Error ? error.message : 'unknown',
    })
  } finally {
    clearTimeout(timer)
  }
}

/**
 * Centralized logout routine. Call this instead of rolling per-call-site
 * cleanup. Revokes the Better Auth session (best effort), clears persisted app
 * state, schedules RxDB deletion on next boot, and clears the auth tokens.
 *
 * Callers that trigger a full-page reload MUST await this so the IndexedDB
 * auth backup is gone before the reload — otherwise restoreFromIndexedDB()
 * on next boot will re-authenticate the user.
 */
export const performLogout = async (): Promise<void> => {
  // Cancel proactive refresh first so it can't re-mint a JWT mid-logout. (An
  // in-flight one is discarded once the session token is gone.)
  cancelScheduledRefresh()
  await revokeServerSession(getSessionToken())

  try {
    useLoginStore.getState().reset()
  } catch (error) {
    logger.logError(error as Error, LogCategory.AUTH_SESSION, {
      component: 'performLogout',
      action: 'resetLoginStore',
    })
  }

  try {
    for (const key of PERSISTED_STORE_KEYS) {
      window.localStorage.removeItem(key)
    }
    for (const key of DIRECT_STORAGE_KEYS) {
      window.localStorage.removeItem(key)
    }
    // Sweep per-workspace purge metadata keys written by PurgeService.
    const purgeKeysToRemove: Array<string> = []
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i)
      if (key && key.startsWith(PURGE_METADATA_KEY_PREFIX)) {
        purgeKeysToRemove.push(key)
      }
    }
    for (const key of purgeKeysToRemove) {
      window.localStorage.removeItem(key)
    }
    // Schedule RxDB database deletion on next boot. handlePendingResets()
    // runs before RxDB initializes (see rxdb/index.ts) and will drop all
    // rxdb-dexie-* databases, preventing the previous user's cached docs
    // from leaking into the next session.
    window.localStorage.setItem('moly_reset_rxdb_pending', 'true')
  } catch (error) {
    logger.logError(error as Error, LogCategory.AUTH_SESSION, {
      component: 'performLogout',
      action: 'clearLocalStorage',
    })
  }

  try {
    window.sessionStorage.clear()
  } catch (error) {
    logger.logError(error as Error, LogCategory.AUTH_SESSION, {
      component: 'performLogout',
      action: 'clearSessionStorage',
    })
  }

  await clearAuthStorage()
}
